import { Fragment } from 'react';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Icon, IconProps } from '../../display/Icon';
import { LegacyButton } from '../../navigation/LegacyButton';
import { Link } from '../../navigation/Link';
import { type LegacyColorName, LegacyColorMap } from '@/theme/LegacyColorMap';

import styles from './Block.module.scss';

export type Props = {
  /**
   * Heading text content.
   */
  heading?: string;
  /**
   * Description text displayed below the heading.
   */
  description?: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: string;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Text content of the secondary CTA button.
   */
  secondaryCtaText?: string;
  /**
   * href for the secondary CTA button.
   */
  secondaryCtaHref?: string;
  /**
   * If true makes the whole block clickable.
   */
  isLink?: boolean;
  /**
   * Centers content
   */
  isCentered?: boolean;
  /**
   * Media displayed above a heading.
   */
  media?: React.ReactNode;
  /**
   * Content text displayed below the description.
   */
  smallText?: string;
  /**
   * String representing the name of icon to display.
   */
  iconName?: IconProps['name'];
  /**
   * String representing the color of icon to display.
   */
  iconColor?: LegacyColorName;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Secondary CTA button click handler.
   */
  onSecondaryCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * Secondary CTA button keyDown handler.
   */
  onSecondaryCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
};

type ContentProps = Pick<
  Props,
  'iconName' | 'iconColor' | 'media' | 'heading' | 'description' | 'smallText'
>;

type PrimaryCTAProps = Pick<
  Props,
  'ctaHref' | 'onCtaClick' | 'onCtaKeyDown' | 'ctaText'
>;

type SecondaryCTAProps = Pick<
  Props,
  | 'secondaryCtaHref'
  | 'onSecondaryCtaClick'
  | 'onSecondaryCtaKeyDown'
  | 'secondaryCtaText'
>;

const Content = ({
  iconName,
  iconColor,
  media,
  heading,
  description,
  smallText,
}: ContentProps) => {
  return (
    <Fragment>
      {iconName && (
        <div data-testid="icon" className={styles.iconContainer} data-zds>
          <Icon
            color={iconColor ? LegacyColorMap[iconColor] : undefined}
            name={iconName}
            size={50}
          />
        </div>
      )}
      {media && (
        <div className={styles.mediaContainer} data-zds>
          {media}
        </div>
      )}
      {heading && (
        <HeadingLevel>
          <Heading className={styles.heading}>{heading}</Heading>
        </HeadingLevel>
      )}
      {description && (
        <div className={styles.description} data-zds>
          {description}
        </div>
      )}
      {smallText && (
        <p className={styles.smallText} data-zds>
          {smallText}
        </p>
      )}
    </Fragment>
  );
};

const PrimaryCTA = ({
  ctaHref,
  onCtaClick,
  onCtaKeyDown,
  ctaText,
}: PrimaryCTAProps) => (
  <div className={styles.primaryCtaContainer} data-zds>
    <LegacyButton
      color="primary"
      href={ctaHref}
      onClick={onCtaClick}
      onKeyDown={onCtaKeyDown}
      data-testid="cta-button"
    >
      {ctaText}
    </LegacyButton>
  </div>
);

const SecondaryCTA = ({
  secondaryCtaHref,
  onSecondaryCtaClick,
  onSecondaryCtaKeyDown,
  secondaryCtaText,
}: SecondaryCTAProps) => {
  return (
    <div className={styles.secondaryCtaContainer} data-zds>
      <Link
        data-testid="cta-secondary-button"
        href={secondaryCtaHref}
        onClick={onSecondaryCtaClick}
        onKeyDown={onSecondaryCtaKeyDown}
        weight="bold"
      >
        <div className={styles.secondaryCtaText} data-zds>
          {secondaryCtaText}
        </div>
      </Link>
    </div>
  );
};

export function Block({
  isLink = false,
  isCentered = false,
  description,
  iconName,
  iconColor = 'blue',
  media,
  ctaText,
  ctaHref,
  secondaryCtaText,
  secondaryCtaHref,
  heading,
  smallText,
  onCtaClick,
  onCtaKeyDown,
  onSecondaryCtaClick,
  onSecondaryCtaKeyDown,
}: Props) {
  const hasPrimaryCta = Boolean(ctaText && (ctaHref || onCtaClick));
  const hasSecondaryCta = Boolean(
    secondaryCtaText && (secondaryCtaHref || onSecondaryCtaClick)
  );

  const contentProps: ContentProps = {
    iconName,
    iconColor,
    media,
    heading,
    description,
    smallText,
  };
  const primaryCTAProps: PrimaryCTAProps = {
    ctaHref,
    onCtaClick,
    onCtaKeyDown,
    ctaText,
  };
  const secondaryCTAProps: SecondaryCTAProps = {
    secondaryCtaHref,
    onSecondaryCtaClick,
    onSecondaryCtaKeyDown,
    secondaryCtaText,
  };

  return (
    <div
      className={styles.container}
      data-zds
      style={{
        '--_zds-block-align-items': isCentered ? 'center' : 'flex-start',
        '--_zds-block-text-align': isCentered ? 'center' : 'left',
      }}
    >
      {isLink ? (
        <Link
          className={styles.link}
          data-testid="link-wrapper"
          href={ctaHref}
          onClick={onCtaClick}
          onKeyDown={onCtaKeyDown}
        >
          <Content {...contentProps} />
        </Link>
      ) : (
        <Fragment>
          <Content {...contentProps} />
          {hasPrimaryCta && <PrimaryCTA {...primaryCTAProps} />}
          {hasSecondaryCta && <SecondaryCTA {...secondaryCTAProps} />}
        </Fragment>
      )}
    </div>
  );
}
